
import { motion } from 'framer-motion';

interface TestIntroProps {
  onStart: () => void;
}

const TestIntro = ({ onStart }: TestIntroProps) => {
  return (
    <section className="py-24 min-h-screen flex items-center justify-center bg-gradient-to-br from-accent/5 to-white">
      <div className="max-w-5xl mx-auto px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          {/* Icon */}
          <div className="w-28 h-28 mx-auto mb-8 bg-gradient-to-br from-primary to-primary-dark rounded-2xl flex items-center justify-center shadow-xl">
            <i className="ri-bar-chart-box-line text-6xl text-white"></i>
          </div>

          {/* Main Title */}
          <h1 className="text-5xl font-bold text-primary mb-4 leading-tight">
            1039可行性判断引擎
          </h1>

          {/* Subtitle */}
          <p className="text-2xl text-primary/80 mb-6 font-medium">
            3分钟智能评估·明确结论·精准路径
          </p>

          {/* Description */}
          <p className="text-lg text-neutral-600 mb-16 leading-relaxed max-w-3xl mx-auto">
            这不是普通问卷，而是1039市场采购贸易专业评估工具。通过智能分析您的业务结构、合规边界和落地条件，输出专业判断结果和定制化行动方案。
          </p>

          {/* Core Features */}
          <div className="grid grid-cols-4 gap-8 mb-16">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-accent to-accent-dark rounded-xl flex items-center justify-center">
                <i className="ri-time-line text-3xl text-white"></i>
              </div>
              <h3 className="font-bold text-primary mb-2 text-lg">3-5分钟</h3>
              <p className="text-sm text-neutral-600">快速完成评估<br/>高效决策支持</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="font-bold text-primary mb-2 text-lg">合规边界</h3>
              <p className="text-sm text-neutral-600">智能筛选判断<br/>风险提前识别</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                <i className="ri-target-line text-3xl text-white"></i>
              </div>
              <h3 className="font-bold text-primary mb-2 text-lg">明确结论</h3>
              <p className="text-sm text-neutral-600">适合/条件适合/暂不适合<br/>专业判断建议</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                <i className="ri-route-line text-3xl text-white"></i>
              </div>
              <h3 className="font-bold text-primary mb-2 text-lg">行动路径</h3>
              <p className="text-sm text-neutral-600">定制化方案<br/>下一步指引</p>
            </motion.div>
          </div>

          {/* Value Props */}
          <div className="grid grid-cols-3 gap-8 mb-16">
            <div className="text-left">
              <div className="flex items-start gap-4 mb-4">
                <i className="ri-user-settings-line text-3xl text-accent mt-1"></i>
                <div>
                  <h4 className="text-xl font-bold text-primary mb-2">客户自评工具</h4>
                  <p className="text-neutral-600">无需销售介入，独立完成专业评估</p>
                </div>
              </div>
            </div>
            <div className="text-left">
              <div className="flex items-start gap-4 mb-4">
                <i className="ri-database-2-line text-3xl text-accent mt-1"></i>
                <div>
                  <h4 className="text-xl font-bold text-primary mb-2">数据直接沉淀</h4>
                  <p className="text-neutral-600">测试结果可作为系统使用起点</p>
                </div>
              </div>
            </div>
            <div className="text-left">
              <div className="flex items-start gap-4 mb-4">
                <i className="ri-shield-keyhole-line text-3xl text-accent mt-1"></i>
                <div>
                  <h4 className="text-xl font-bold text-primary mb-2">合规边界筛选</h4>
                  <p className="text-neutral-600">提前识别风险，专业劝退不适合客户</p>
                </div>
              </div>
            </div>
          </div>

          {/* Start Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onStart}
            className="px-16 py-6 bg-gradient-to-r from-accent to-accent-dark text-primary text-xl font-bold rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center gap-4 mx-auto cursor-pointer whitespace-nowrap mb-8"
          >
            <i className="ri-play-line text-2xl"></i>
            开始可行性评估
            <i className="ri-arrow-right-line text-2xl"></i>
          </motion.button>

          {/* Compliance Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 max-w-2xl mx-auto">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-2xl text-amber-600 mt-1"></i>
              <div className="text-left">
                <p className="text-sm text-amber-800 font-medium mb-1">合规声明</p>
                <p className="text-sm text-amber-700">
                  评估结果基于当前填报信息的初步判断，最终业务合规性以海关总署及相关监管部门要求为准
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestIntro;
